import {
  Box,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CreditCardModel,
  CreditCardType,
  UpdateCardRequest,
  useDeleteCard,
  useUpdateCard,
} from '@/logic/services/credit-cards';
import { FC, useCallback, useState } from 'react';
import { useDialog, useSnack } from '@/logic/state';

import { FormattedMessage } from 'react-intl';
import Icon24 from '../shared/icon-24';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import axios from 'axios';
import { getCreditCardIcon } from '@/logic/shared';
import { pxToRem } from '@/theme/typography';

import shallow from 'zustand/shallow';
import { useCreditCardTitle } from '@/logic/hooks/use-credit-card-title';
import ImageBoxFuture from '../shared/image-box-future';

const DeleteCardIcon = '/icons/profile/credit-card/redDelete.svg';
const EditCardIcon = '/icons/profile/credit-card/edit-card.svg';
const removeDefaultCardIcon = '/icons/profile/credit-card/remove-default.svg';
const setDefaultIcon = '/icons/profile/credit-card/set-default.svg';

interface ProfileCreditCardModel extends UpdateCardRequest {
  id: number;
}

interface CreditCardProps {
  cardDetails: CreditCardModel;
  refetchCards: () => void;
}

export const CreditCardItem: FC<CreditCardProps> = ({ cardDetails, refetchCards }) => {
  const theme = useTheme();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const setDialog = useDialog(s => s.setDialog, shallow);

  const { getCreditCardTitle } = useCreditCardTitle();

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const deleteCard = useDeleteCard();
  const updateCard = useUpdateCard();

  const deleteCreditCard = (id: number) => {
    setDialog({
      wrapper: true,
      isDeleteCardConfirmationDialog: true,
      deleteCardConfirmationDialogPayload: {
        isLoading: true,
      },
    });

    deleteCard.safeMutateAsync(
      {
        query: {
          id,
        },
      },
      {
        onSuccess: () => {
          setSnack({
            isOpen: true,
            message: 'my_credit_cards.deleted.successfully',
            type: 'success',
            id: 'credit-card-deleted-success',
          });
          setDialog({});
        },
        onError: error => {
          if (axios.isAxiosError(error)) {
            setDialog({
              wrapper: true,
              isDeleteCardConfirmationDialog: true,
              deleteCardConfirmationDialogPayload: {
                isLoading: false,
              },
            });
          }
        },
        onSettled: () => refetchCards(),
      }
    );
  };

  const updateCreditCardDefaultStatus = (item: ProfileCreditCardModel) => {
    updateCard.safeMutateAsync(
      {
        query: { id: item?.id },
        data: {
          name: item.name,
          isDefault: !item.isDefault,
        },
      },
      {
        onSuccess: () => {
          setSnack({
            isOpen: true,
            message: !item.isDefault
              ? 'my_credit_cards.set_default_successfully'
              : 'my_credit_cards.remove_default_successfully',
            type: 'success',
          });
        },
        onSettled: () => refetchCards(),
      }
    );
  };

  const openDeleteCardConfirmationDialog = useCallback(
    (cardId: number) =>
      setDialog({
        wrapper: true,
        isDeleteCardConfirmationDialog: true,
        deleteCardConfirmationDialogPayload: {
          handleOkayClick: () => deleteCreditCard(cardId),
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setDialog]
  );

  const openEditCreditCardForm = useCallback(
    (cardDetails: CreditCardModel) => {
      setDialog({
        wrapper: true,
        isEditCreditCardForm: true,
        editCreditCardFormPayload: {
          id: cardDetails.id,
          cardName: cardDetails.name,
          cardNumber: cardDetails.last4,
          cardHolder: cardDetails.cardHolder,
          expiryDate: `${
            +cardDetails.expiryMonth >= 10
              ? cardDetails.expiryMonth
              : `0${cardDetails.expiryMonth}`
          }/${cardDetails.expiryYear.slice(-2)}`,
          cardType: cardDetails.scheme,
          isDefault: cardDetails.isDefault,
        },
      });
    },
    [setDialog]
  );

  return (
    <ListItem
      disablePadding
      sx={{
        mb: { tablet: pxToRem(13), mobile: '13px' },
        borderBottom: '1px solid #EAEAEA',
      }}
    >
      <ImageBoxFuture
        img={getCreditCardIcon(
          cardDetails?.isMada ? CreditCardType.Mada : cardDetails.scheme
        )}
        title={getCreditCardTitle(
          cardDetails?.isMada ? CreditCardType.Mada : cardDetails.scheme
        )}
        objectFit={cardDetails?.isMada ? 'contain' : 'cover'}
        sx={{
          mr: '17px',
          width: { tablet: pxToRem(46), mobile: '46px' },
          height: { tablet: pxToRem(34), mobile: '34px' },
        }}
      />

      <ListItemText
        primary={
          <Typography
            component='h4'
            sx={{
              fontSize: { tablet: pxToRem(18), mobile: '16px' },
              fontWeight: 400,
              lineHeight: '24px',
              color: theme.palette.grey[80],
              overflowWrap: 'anywhere',
            }}
          >
            {cardDetails.name}
          </Typography>
        }
        secondary={
          <Typography
            component='p'
            sx={{
              fontSize: { tablet: pxToRem(16), mobile: '14px' },
              fontWeight: 400,
              lineHeight: { tablet: '150%', mobile: '24px' },
              color: theme.palette.grey[50],
            }}
          >
            <Typography component='span' dir='ltr' sx={{}}>
              **** {cardDetails.last4}
            </Typography>
            {cardDetails.isDefault && (
              <FormattedMessage id='my_credit_cards.default_card' />
            )}
          </Typography>
        }
      />
      <Box
        component='div'
        sx={{
          width: pxToRem(24),
          position: 'relative',
          display: 'flex',
          justifyContent: 'flex-end',
          '& button ': {
            width: pxToRem(12),
            p: 0,
          },
        }}
      >
        <Tooltip title={<FormattedMessage id='global.options' />}>
          <IconButton id='credit-card-options' onClick={handleClick} size='small'>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          id='account-menu'
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: 'visible',
              boxShadow: ' 0px 2px 5px rgba(0, 0, 0, 0.109051)',
              py: { tablet: pxToRem(8), mobile: '8px' },
              '& li': {
                px: { tablet: pxToRem(16), mobile: '12px' },
                py: 0,
                fontSize: {
                  tablet: pxToRem(18),
                  mobile: '14px',
                  color: theme.palette.grey[80],
                },
              },
              '& hr': {
                my: { tablet: pxToRem(18.5), mobile: '20px' },
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem
            id='delete-credit-card'
            onClick={() => openDeleteCardConfirmationDialog(cardDetails.id)}
          >
            <ListItemIcon>
              <Icon24 icon={DeleteCardIcon} title='global.delete_icon' />
            </ListItemIcon>

            <FormattedMessage id='my_credit_cards.delete_card' />
          </MenuItem>
          <Divider
            sx={{
              borderColor: '#EAEAEA',
            }}
          />
          <MenuItem
            id='edit-credit-card'
            onClick={() => openEditCreditCardForm(cardDetails)}
          >
            <ListItemIcon>
              <Icon24 icon={EditCardIcon} title='alt.edit_icon' />
            </ListItemIcon>

            <FormattedMessage id='my_credit_cards.edit_card' />
          </MenuItem>
          <Divider />
          {cardDetails.isDefault ? (
            <MenuItem
              id='mark-default-credit-card'
              onClick={() => {
                updateCreditCardDefaultStatus({
                  id: cardDetails?.id,
                  name: cardDetails?.name,
                  isDefault: cardDetails?.isDefault,
                });
              }}
            >
              <ListItemIcon>
                <Icon24 icon={removeDefaultCardIcon} title='alt.remove_default_icon' />
              </ListItemIcon>

              <FormattedMessage id='my_credit_cards.remove_default' />
            </MenuItem>
          ) : (
            <MenuItem
              id='mark-default-credit-card'
              onClick={() => {
                updateCreditCardDefaultStatus({
                  id: cardDetails?.id,
                  name: cardDetails?.name,
                  isDefault: cardDetails?.isDefault,
                });
              }}
            >
              <ListItemIcon>
                <Icon24 icon={setDefaultIcon} title='alt.set_default_icon' />
              </ListItemIcon>
              <FormattedMessage id='my_credit_cards.set_default' />
            </MenuItem>
          )}
        </Menu>
      </Box>
    </ListItem>
  );
};
